/**
 * MESH Score Calculator
 *
 * Calculates MESH scores from assessment data.
 */

import type { TenantContext } from '@tonnie/foundation';
import { requireTenantId, assertTenantIsolation, TenantIsolationError } from '@tonnie/foundation';
import type {
  MESHScores,
  MESHDimension,
  Trend,
  MESHInput,
  MESHHistoryEntry,
  MESHRepository,
} from './types';

// Re-export types
export type { MESHScores, MESHDimension, Trend };

const DIMENSION_LABELS: Record<MESHDimension, string> = {
  M: 'Motivation',
  E: 'English',
  S: 'Soft Skills',
  H: 'Hard Skills',
};

/**
 * Calculates MESH scores from input data.
 * Validates tenant context before processing.
 *
 * @param input - MESH calculation input (must include tenantId)
 * @param context - Tenant context for isolation validation
 * @returns Calculated MESH scores
 * @throws TenantIsolationError if tenant validation fails
 */
export function calculateMESHScores(input: MESHInput, context: TenantContext): MESHScores {
  const tenantId = requireTenantId(context);

  if (input.tenantId !== tenantId) {
    throw new TenantIsolationError(
      `Cannot calculate MESH for tenant ${input.tenantId} in context of tenant ${tenantId}`
    );
  }

  return {
    M: calculateMotivationScore(input.motivationAnswers),
    E: calculateEnglishScore(input.englishTestScores),
    S: calculateSoftSkillsScore(input.softSkillsAssessment),
    H: calculateHardSkillsScore(input.hardSkillsData),
  };
}

/**
 * Pure calculation function - no tenant context needed.
 */
function calculateMotivationScore(answers: Record<string, number>): number {
  const values = Object.values(answers);
  if (values.length === 0) return 0;

  const sum = values.reduce((acc, val) => acc + val, 0);
  const normalized = (sum / values.length) * 20; // Scale 0-5 to 0-100
  return Math.round(Math.min(100, Math.max(0, normalized)));
}

function calculateEnglishScore(scores: Record<string, number>): number {
  const values = Object.values(scores);
  if (values.length === 0) return 0;

  const sum = values.reduce((acc, val) => acc + val, 0);
  return Math.round(Math.min(100, Math.max(0, sum / values.length)));
}

function calculateSoftSkillsScore(assessment: Record<string, number>): number {
  const values = Object.values(assessment);
  if (values.length === 0) return 0;

  const sum = values.reduce((acc, val) => acc + val, 0);
  const normalized = (sum / values.length) * 20;
  return Math.round(Math.min(100, Math.max(0, normalized)));
}

function calculateHardSkillsScore(data: Record<string, number>): number {
  const values = Object.values(data);
  if (values.length === 0) return 0;

  const sum = values.reduce((acc, val) => acc + val, 0);
  return Math.round(Math.min(100, Math.max(0, sum / values.length)));
}

/**
 * Pure function - returns label for a MESH dimension.
 */
export function getMESHDimensionLabel(dimension: MESHDimension): string {
  return DIMENSION_LABELS[dimension];
}

/**
 * Pure function - calculates trend between two scores.
 */
export function getMESHDimensionTrend(current: number, previous: number): Trend {
  const diff = current - previous;
  if (diff > 5) return 'up';
  if (diff < -5) return 'down';
  return 'stable';
}

/**
 * Retrieves MESH history for a talent.
 * Requires TenantContext because it accesses data.
 */
export async function getMESHHistory(
  talentId: string,
  context: TenantContext,
  repository: MESHRepository
): Promise<MESHHistoryEntry[]> {
  requireTenantId(context);

  const history = await repository.findByTalent(talentId, context);

  // Validate all returned entities
  for (const entry of history) {
    assertTenantIsolation(entry, context);
  }

  return history;
}
